// src/pages/gt3x/standings/StandingsTeam.jsx
import { useEffect, useMemo, useState, Fragment } from "react";
import { fetchSheetViaProxy } from "../../lib/sheets";

const RANGE = "standings_team!A1:AI100";

// Team-Mitglieder Quelle:
const TEAMS_RANGE = "Teams!E1:G1000"; // E = Teamname, F = Fahrername

// "A" -> 1, "Z" -> 26, "AA" -> 27, ...
const colIndex = (letter) => {
    let n = 0;
    for (let i = 0; i < letter.length; i++) n = n * 26 + (letter.charCodeAt(i) - 64);
    return n;
};

const LETTER_AA = colIndex("AA"); // 27 (letzte sichtbare Spalte)
//const LETTER_AB = colIndex("AB"); // 28 (Teamlogo-URL, unsichtbar)
const LETTER_M  = colIndex("M");  // 13 (R1)
const LETTER_X  = colIndex("X");  // 24 (R12)
const LETTER_Y  = colIndex("Y");  // 25 (Drop1)
//const LETTER_AZ = colIndex("AZ"); // 52 (falls gebraucht, wir begrenzen auf AA visuell)

// Hilfsfunktionen
const isNumeric = (v) => v !== null && v !== "" && !isNaN(Number(v));
const norm = (s) => String(s ?? "").trim().toLowerCase();

function computeTop3ByColumn(rowsAsArrays, startCol, endCol) {
    const map = new Map();
    for (let c = startCol; c <= endCol; c++) {
        const nums = rowsAsArrays
            .map(arr => arr[c - 1])
            .filter(v => v !== null && v !== undefined && v !== "")
            .map(v => Number(v))
            .filter(v => !Number.isNaN(v));
        if (!nums.length) { map.set(c, null); continue; }

        const uniqDesc = Array.from(new Set(nums)).sort((a, b) => b - a);
        map.set(c, {
            gold:   uniqDesc[0] ?? null,
            silver: uniqDesc[1] ?? null,
            bronze: uniqDesc[2] ?? null,
        });
    }
    return map;
}

// Helfer: Tool-Response ({header, rows}) -> Array<Array>
function toRowsAsArrays(sheetData) {
    const headers = sheetData?.header || [];
    const rowsObj = sheetData?.rows || [];
    return rowsObj.map((r) => headers.map((h) => r[h]));
}

export default function StandingsTeam() {
    const [data, setData] = useState({ header: [], rows: [] });
    const [teamsData, setTeamsData] = useState({ header: [], rows: [] });

    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");
    const [noData, setNoData] = useState(false);

    // Toggle je Teamname aus standings (Spalte D)
    const [openTeams, setOpenTeams] = useState({});

    const toggleTeam = (teamName) => {
        const key = norm(teamName);
        setOpenTeams((prev) => ({ ...prev, [key]: !prev[key] }));
    };

    useEffect(() => {
        (async () => {
            try {
                // beide Ranges laden
                const [resStandings, resTeams] = await Promise.all([
                    fetchSheetViaProxy({ range: RANGE }),
                    fetchSheetViaProxy({ range: TEAMS_RANGE }),
                ]);

                setData(resStandings);
                setTeamsData(resTeams);
            } catch (e) {
                const msg = e?.message || String(e);
                // Sonderfall: Range existiert noch nicht -> freundliche Empty-Message
                if (msg.includes("Unable to parse range")) {
                    setNoData(true);
                } else {
                    setError(msg);
                }
            } finally {
                setLoading(false);
            }
        })();
    }, []);

    const {
        visibleHeaders,
        idxTeamLogoUrl,
        rowsAsArrays,
        top3PerRaceColumn,
        completedRuns,
        teamToDrivers,
        visibleColNumbers,
    } = useMemo(() => {
        const headers = data.header || [];

        // Sichtbar A..AA (27 Spalten)
        const visibleHeaders = headers.slice(0, LETTER_AA);

        // Teamlogo-URL liegt in AB
        const idxTeamLogoUrl = colIndex("AB") - 1;

        // rowsAsArrays in Index-Form
        const rowsAsArrays = (data.rows || []).map((r) => headers.map((h) => r[h]));

        // Top3-Berechnung für Race-Spalten M..X
        const top3PerRaceColumn = computeTop3ByColumn(rowsAsArrays, LETTER_M, LETTER_X);

        // Anzahl bereits gefahrener Läufe (mind. eine Zahl in der Spalte)
        let completedRuns = 0;
        for (let c = LETTER_M; c <= LETTER_X; c++) {
            const anyNumberInCol = rowsAsArrays.some(row => {
                const v = row[c - 1];
                return v !== null && v !== "" && !Number.isNaN(Number(v));
            });
            if (anyNumberInCol) completedRuns++;
        }

        // Sichtbare ColNumbers (wegen "Spalte E skippen")
        const visibleColNumbers = visibleHeaders
            .map((_, i) => i + 1)
            .filter((colNumber) => colNumber !== 5 && colNumber !== 8);

        // Team -> Fahrer mapping aus Teams-Tab (E=Team, F=Fahrer)
        const teamsArr = toRowsAsArrays(teamsData);
        const teamToDrivers = new Map();

        for (const row of teamsArr) {
            const team = String(row[0] ?? "").trim();      // E
            const driver = String(row[1] ?? "").trim();    // F
            const pointsRaw = row[2];                      // G
            const points = (pointsRaw == null || String(pointsRaw).trim() === "")
                ? ""
                : String(pointsRaw).trim();

            // Skip leere Zeilen / offensichtliche Header
            if (!team || !driver) continue;
            if (norm(team) === "team" && norm(driver).includes("fahrer")) continue;

            const k = norm(team);
            if (!teamToDrivers.has(k)) teamToDrivers.set(k, []);
            teamToDrivers.get(k).push({ name: driver, points });
        }

        // Duplikate entfernen, Reihenfolge behalten
        for (const [k, arr] of teamToDrivers.entries()) {
            const seen = new Set();
            teamToDrivers.set(
                k,
                arr.filter((d) => {
                    const dk = norm(d.name);
                    if (!dk || seen.has(dk)) return false;
                    seen.add(dk);
                    return true;
                })
            );
        }

        return {
            visibleHeaders,
            idxTeamLogoUrl,
            rowsAsArrays,
            top3PerRaceColumn,
            completedRuns,
            teamToDrivers,
            visibleColNumbers,
        };
    }, [data, teamsData]);

    if (loading) return <div className="card"><p>Lade Standings...</p></div>;
    if (noData) {
        return (
            <div className="card">
                <h2>Standings – TEAM</h2>
                <p className="muted">
                    Aktuell sind noch keine Rennergebnisse im System.
                    Sobald das erste Rennen gefahren ist, erscheinen hier die TEAM-Standings der GT3 Xperience.
                </p>
            </div>
        );
    }
    if (error)   return <div className="card"><p style={{ color:"#f55" }}>Fehler: {error}</p></div>;

    // Render: Extra Driver-Zeile (unter Team)
    // Render: Extra Driver-Zeile (unter Team)  ✅ jetzt mit Punkten
    const renderDriverRow = (driver, key) => {
        // driver kann Objekt sein: { name, points } oder ein String (Fallback)
        const name = typeof driver === "string" ? driver : (driver?.name ?? "");
        const points = typeof driver === "string" ? "" : (driver?.points ?? "");

        const label = points && String(points).trim() !== ""
            ? `• ${name} - ${String(points).trim()} Punkte`
            : `• ${name}`;

        return (
            <tr key={key} className="team-driver-row">
                {visibleColNumbers.map((colNumber, idx) => {
                    // Spalte D (colNumber=4) bekommt den eingerückten Fahrernamen + Punkte
                    if (colNumber === 4) {
                        return (
                            <td key={idx} className="align-left">
                                <span className="driver-name">{label}</span>
                            </td>
                        );
                    }

                    // Teamlogo-Spalte (C) leer lassen
                    if (colNumber === 3) return <td key={idx} className="icon" />;

                    // Rest leer
                    return <td key={idx} />;
                })}
            </tr>
        );
    };


    return (
        <>
            <div className="card">
                <h2>Standings <span className="pill pill--team">TEAMS</span></h2>
                <p className="muted">
                    Teamwertung nach Abschluss des {completedRuns}. Laufes präsentiert von BavarianSimTec <br/>
                    Die Teamwertung wird aus der Gesamtwertung des Laufes gebildet mit den Top 15 Fahrern. Pro Team können pro Lauf jedoch maximal zwei Fahrer gewertet werden.
                </p>
            </div>

            <div className="fancyTable">
                <table className="tbl">
                    <thead>
                    <tr>
                        {visibleHeaders.map((h, i) => {
                            const colNumber = i + 1;
                            if (colNumber === 5 || colNumber === 8) return null; // Spalte E + H skippen

                            // Kopfzeilen-Labels anpassen
                            // A Pos, B Δ, C (Icon leer, zeigt Teamlogo), D Team, E Fahrer (breit), F Pts, G Diff, H Inc, I R, J W, K P, L DNF
                            const label =
                                colNumber === 1 ? "Pos" :
                                    colNumber === 2 ? "Δ"   :
                                        colNumber === 6 ? "Pts" :
                                            colNumber === 7 ? "Diff":
                                                colNumber === 8 ? "" :
                                                    colNumber === 9 ? "R"   :
                                                        colNumber === 10 ? "W"  :
                                                            colNumber === 11 ? "P"  :
                                                                colNumber === 12 ? "DNF":
                                                                    h;

                            // Rennspalten: M..X (13..24)
                            const isRace    = colNumber >= LETTER_M && colNumber <= LETTER_X;
                            const raceLeft  = colNumber === LETTER_M;
                            const raceRight = colNumber === LETTER_X;
                            const evenStripe = isRace && ((colNumber - LETTER_M) % 2 === 0);

                            // Linksbündig A (Pos), D (Team), E (Fahrer)
                            const leftAligned = (colNumber === 1 || colNumber === 4 || colNumber === 5);

                            // C = Teamlogo-Iconspalte
                            const thClass = [
                                isRace ? "race-col" : "",
                                raceLeft ? "race-left" : "",
                                raceRight ? "race-right" : "",
                                evenStripe ? "race-alt" : "",
                                (colNumber === 3) ? "icon" : "",
                                leftAligned ? "align-left" : "",
                            ].filter(Boolean).join(" ");

                            return <th key={i} className={thClass}>{label}</th>;
                        })}
                    </tr>
                    </thead>

                    <tbody>
                    {rowsAsArrays.map((arr, ri) => {
                        const teamName = String(arr[3] ?? "").trim(); // Spalte D
                        const teamKey = norm(teamName);
                        const isOpen = !!openTeams[teamKey];

                        const drivers = teamToDrivers.get(teamKey) || [];

                        return (
                            <Fragment key={ri}>
                                <tr>
                                    {visibleHeaders.map((_, i) => {
                                        const colNumber = i + 1;
                                        if (colNumber === 5 || colNumber === 8) return null; // Spalte E + H skippen
                                        const val = arr[i];

                                        // C: Teamlogo aus AB
                                        if (colNumber === 3) {
                                            const src = arr[idxTeamLogoUrl] || "";
                                            return (
                                                <td key={i} className="icon">
                                                    {src ? <img src={src} alt="Team" className="teamlogo lg" loading="lazy" /> : null}
                                                </td>
                                            );
                                        }

                                        // Rennspalten M..X mit Top-3-Highlight
                                        const isRace    = colNumber >= LETTER_M && colNumber <= LETTER_X;
                                        const raceLeft  = colNumber === LETTER_M;
                                        const raceRight = colNumber === LETTER_X;
                                        const evenStripe = isRace && ((colNumber - LETTER_M) % 2 === 0);

                                        let tierClass = "";
                                        if (isRace && val !== "" && val !== null && val !== undefined) {
                                            const numVal = Number(val);
                                            const t3 = top3PerRaceColumn.get(colNumber);
                                            if (t3 && !Number.isNaN(numVal)) {
                                                if (numVal === t3.gold)        tierClass = "race-gold";
                                                else if (numVal === t3.silver) tierClass = "race-silver";
                                                else if (numVal === t3.bronze) tierClass = "race-bronze";
                                            }
                                        }

                                        // Linksbündig A (Pos), D (Team), E (Fahrer)
                                        const leftAligned = (colNumber === 1 || colNumber === 4 || colNumber === 5);

                                        // Delta (B) einfärben
                                        let deltaClass = "";
                                        if (colNumber === 2) {
                                            const n = Number(val);
                                            if (!isNaN(n)) {
                                                if (n > 0) deltaClass = "delta-up";
                                                else if (n < 0) deltaClass = "delta-down";
                                            }
                                        }

                                        // Kompakte Metriken: F..L (Points..DNFs = 6..12)
                                        const compactMetrics = (colNumber >= 6 && colNumber <= 12) ? "compact-metrics" : "";

                                        const numeric =
                                            isNumeric(val) ||
                                            (colNumber >= 6 && colNumber <= 12) || // Points..DNFs
                                            isRace ||
                                            (colNumber >= LETTER_Y && colNumber <= LETTER_AA); // Drops

                                        const tdClass = [
                                            numeric ? "num" : "",
                                            isRace ? "race-col" : "",
                                            raceLeft ? "race-left" : "",
                                            raceRight ? "race-right" : "",
                                            evenStripe ? "race-alt" : "",
                                            tierClass,
                                            leftAligned ? "align-left" : "",
                                            (colNumber === 6) ? "points-highlight" : "", // Points (F)
                                            (colNumber === 1) ? "pos-highlight" : "",    // Pos (A)
                                            deltaClass,
                                            compactMetrics,
                                        ].filter(Boolean).join(" ");

                                        // Teamspalte D: PLUS davor (Teamname bleibt wie vorher!)
                                        if (colNumber === 4) {
                                            return (
                                                <td key={i} className={tdClass}>
                                                    <button
                                                        type="button"
                                                        className="team-expand-btn"
                                                        aria-label={isOpen ? "Team einklappen" : "Team ausklappen"}
                                                        onClick={(e) => {
                                                            e.preventDefault();
                                                            e.stopPropagation();
                                                            toggleTeam(teamName);
                                                        }}
                                                    >
                                                        {isOpen ? "−" : "+"}
                                                    </button>
                                                    <span className="team-name">{val}</span>
                                                </td>
                                            );
                                        }

                                        // Fahrer-Spalte (E) gibt es in der Anzeige nicht (skip), daher nichts extra

                                        const content = (colNumber === 5 && typeof val === "string" && val.length > 0)
                                            ? <span title={val}>{val}</span>
                                            : val;

                                        return <td key={i} className={tdClass}>{content}</td>;
                                    })}
                                </tr>

                                {/* Ausklappbereich: Fahrerlisten aus Teams-Tab */}
                                {isOpen && (
                                    <>
                                        {drivers.length > 0 ? (
                                            drivers.map((d, di) => renderDriverRow(d, `drv-${ri}-${di}`))
                                        ) : (
                                            renderDriverRow("keine Fahrer im Teams-Tab gefunden", `drv-empty-${ri}`)
                                        )}
                                    </>
                                )}
                            </Fragment>
                        );
                    })}
                    </tbody>
                </table>
            </div>
        </>
    );
}
